import React, { useContext, Suspense, lazy } from 'react';
import { QueryClient, QueryClientProvider } from 'react-query';
import { ReactQueryDevtools } from 'react-query/devtools';
import { IntlProvider } from 'react-intl';
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  Outlet,
  Params,
} from 'react-router-dom';
import { ErrorBoundary } from 'react-error-boundary';

import { light_theme } from '@joinforma/ui/constants/themes';
import ThemeProvider from '@joinforma/ui/components/ThemeProvider';

// Vendors
import * as Sentry from '@sentry/react';

//Utils
import * as AuthUtils from 'utils/auth';
import { AccountRoutes, RootRoutes } from 'utils/routeGenerator';

import { FeatureFlag } from 'constants/featureFlag';

//Contexts
import FeatureFlagContext, {
  FeatureFlagContextProvider,
} from 'contexts/FeatureFlagContext';
import { CurrentUserContextProvider } from 'contexts/CurrentUserContext';
import { PretaxContextProvider } from 'contexts/PretaxContext';
import { HsaReimbursementFormContextProvider } from 'contexts/HsaReimbursementFormContext';
import { HsaExpenseFormContextProvider } from 'contexts/HsaExpenseFormContext';

//Components
import DeployListenerLayout from 'layouts/DeployListenerLayout';
import BaseLayout from 'layouts/BaseLayout';
import WelcomeLayout from 'layouts/WelcomeLayout';
import StoreLayout from 'layouts/StoreLayout';
import CardsLayout from 'layouts/CardsLayout';
import SettingsLayout from 'layouts/SettingsLayout';
import ProfileLayout from 'layouts/ProfileLayout';
import EnrollLayout from 'layouts/EnrollLayout';
import FullScreenLayout from 'layouts/FullScreenLayout';
import LoginLayout from 'layouts/LoginLayout';
import SettingsLinkAccountsLayout from 'layouts/SettingsLinkAccountLayout';

import ScrollToTop from 'components/common/ScrollToTop';
import Spinner from 'components/common/Spinner';

//IMP Moving this to lazy loading breaks dropdown and intercom product tour.
//Lazy loading skip components start
//Settings
import SettingsProfilePage from 'components/SettingsProfile';
// Profile
import ProfileSubscriptionList from 'components/ProfileSubscriptionList';
// Lazy loading causes search input to lose focus when the first
// character is typed and the route redirects to /search
import ProductSearchResultsPage from 'components/ProductSearchResults';
import FormaCardListPage from 'components/FormaCardList';
import StoreLandingCompactLayout from 'layouts/StoreLandingCompactLayout';
import StoreLandingLayout from 'layouts/StoreLandingLayout';
import StoreWithBannerLayout from 'layouts/StoreWithBannerLayout';

//Lazy loading skip components ends
const LoginPage = lazy(
  () => import(/* webpackChunkName: "login" */ 'components/Login')
);
const LoginPasswordResetPage = lazy(
  () =>
    import(
      /* webpackChunkName: "login_password_reset" */ 'components/LoginPasswordReset'
    )
);
const Authentication = lazy(
  () => import(/* webpackChunkName: "auth" */ 'components/Authentication')
);
const MemberActivationSuccessPage = lazy(
  () =>
    import(
      /* webpackChunkName: "member_activation_success" */ 'components/MemberActivationSuccess'
    )
);

const MemberActivationPage = lazy(
  () =>
    import(
      /* webpackChunkName: "member_activation" */ 'components/MemberActivation'
    )
);

const MemberOnboarding = lazy(
  () =>
    import(
      /* webpackChunkName: "member_onboarding" */ 'components/MemberOnboarding'
    )
);

const MemberOnboardingReview = lazy(
  () =>
    import(
      /* webpackChunkName: "member_onboarding_review" */ 'components/MemberOnboardingReview'
    )
);

const MemberOnboardingActivate = lazy(
  () =>
    import(
      /* webpackChunkName: "member_onboarding_activate" */ 'components/MemberOnboardingActivate'
    )
);

const ClaimNewPage = lazy(
  () => import(/* webpackChunkName: "claim_new" */ 'components/ClaimNew')
);
const ClaimSuccessPage = lazy(
  () =>
    import(/* webpackChunkName: "claim_success" */ 'components/ClaimSuccess')
);
const ClaimListPage = lazy(
  () => import(/* webpackChunkName: "claim_list" */ 'components/ClaimList')
);
const ClaimDetailEmployerSponsoredPage = lazy(
  () =>
    import(
      /* webpackChunkName: "claim_detail" */ 'components/ClaimDetailEmployerSponsored'
    )
);
const ClaimDetailPretaxClaimPage = lazy(
  () =>
    import(
      /* webpackChunkName: "claim_detail_pretax_claim" */ 'components/ClaimDetailPretaxClaim'
    )
);
const ClaimDetailPretaxTransactionPage = lazy(
  () =>
    import(
      /* webpackChunkName: "claim_detail_pretax_transaction" */ 'components/ClaimDetailPretaxTransaction'
    )
);

const ProfileOrderListPage = lazy(
  () =>
    import(
      /* webpackChunkName: "profile_orders" */ 'components/ProfileOrderList'
    )
);

//Vendors
const VendorVenueSearchPage = lazy(
  () => import(/* webpackChunkName: "venue" */ 'components/VendorVenueSearch')
);
//Pretax Accounts Store Links
const PretaxStoreDetailPage = lazy(
  () =>
    import(
      /* webpackChunkName: "pretax_store_details" */ 'components/PretaxStoreDetail'
    )
);
const ExternalStoreLinkPage = lazy(
  () =>
    import(
      /* webpackChunkName: "external_store_link" */ 'components/ExternalStoreLink'
    )
);

const AccountListPage = lazy(
  () =>
    import(/* webpackChunkName: "account_overview" */ 'components/AccountList')
);

const AccountDetailEmployerSponsored = lazy(
  () =>
    import(
      /* webpackChunkName: "wallet" */ 'components/AccountDetailEmployerSponsored'
    )
);

const AccountDetailPretaxPage = lazy(
  () =>
    import(/* webpackChunkName: "wallet" */ 'components/AccountDetailPretax')
);

const AccountEnrollHsaPage = lazy(
  () =>
    import(
      /* webpackChunkName: "account_enroll_hsa" */ 'components/AccountEnrollHsa'
    )
);

const AccountEnrollHsaSuccessPage = lazy(
  () =>
    import(
      /* webpackChunkName: "account_enroll_hsa_success" */ 'components/AccountEnrollHsaSuccess'
    )
);

const AccountEnrollPretaxPage = lazy(
  () =>
    import(/* webpackChunkName: "wallet" */ 'components/AccountEnrollPretax')
);
const AccountEnrollReviewPretaxPage = lazy(
  () =>
    import(
      /* webpackChunkName: "wallet" */ 'components/AccountEnrollReviewPretax'
    )
);
const AccountEnrollConfirmedPretaxPage = lazy(
  () =>
    import(
      /* webpackChunkName: "wallet" */ 'components/AccountEnrollConfirmedPretax'
    )
);

const HsaInvestmentDetailPage = lazy(
  () =>
    import(
      /* webpackChunkName: "hsa_investment_detail" */ 'components/HsaInvestmentDetail'
    )
);

const HsaSellOrders = lazy(
  () =>
    import(/* webpackChunkName: "hsa_sell_orders" */ 'components/HsaSellOrders')
);

const HsaSellOrdersConfirmation = lazy(
  () =>
    import(
      /* webpackChunkName: "hsa_sell_orders_confirmation" */ 'components/HsaSellOrdersConfirmation'
    )
);

const HsaCashDocumentListPage = lazy(
  () =>
    import(
      /* webpackChunkName: "hsa_cash_document_list" */ 'components/HsaCashDocuments'
    )
);

const HsaInvestmentDocumentListPage = lazy(
  () =>
    import(
      /* webpackChunkName: "hsa_investment_document_list" */ 'components/HsaInvestmentDocuments'
    )
);

const HsaReimbursementNew = lazy(
  () =>
    import(
      /* webpackChunkName: "hsa_document_list" */ 'components/HsaReimbursementNew'
    )
);

const HsaReimbursementConfirm = lazy(
  () =>
    import(
      /* webpackChunkName: "hsa_document_list" */ 'components/HsaReimbursementConfirm'
    )
);

const HsaExpenseNew = lazy(
  () =>
    import(
      /* webpackChunkName: "hsa_document_list" */ 'components/HsaExpenseNew'
    )
);

const HsaExpenseConfirm = lazy(
  () =>
    import(
      /* webpackChunkName: "hsa_document_list" */ 'components/HsaExpenseConfirm'
    )
);

const HsaInvestMoneyPage = lazy(
  () =>
    import(/* webpackChunkName: "hsa_invest_mo" */ 'components/HsaInvestMoney')
);

const HsaInvestmentSuccessPage = lazy(
  () =>
    import(
      /* webpackChunkName: "hsa_investment_success_page" */ 'components/HsaInvestmentSuccess'
    )
);

const HsaInvestmentDeferralPage = lazy(
  () =>
    import(
      /* webpackChunkName: "hsa_investment_deferral_page" */ 'components/HsaInvestmentDeferral'
    )
);

const AlegeusHsaBulkTransferPage = lazy(
  () =>
    import(
      /* webpackChunkName: "hsa_alegeus_bulk_transfer_page" */ 'components/AlegeusHsaBulkTransfer'
    )
);

const AlegeusHsaBulkTransferDeclinePage = lazy(
  () =>
    import(
      /* webpackChunkName: "hsa_decline_alegeus_bulk_transfer_page" */ 'components/AlegeusHsaBulkDeclineTransfer'
    )
);

//Transaction
const ProfileOrderDetail = lazy(
  () =>
    import(
      /* webpackChunkName: "transaction" */ 'components/ProfileOrderDetail'
    )
);

//CardTransaction
const TransactionDetailPosttaxCardPage = lazy(
  () =>
    import(
      /* webpackChunkName: "transaction_post_tax_card_detail" */ 'components/TransactionDetailPosttaxCard'
    )
);

// Pretax Card Transaction
const TransactionDetailPretaxCardPage = lazy(
  () =>
    import(
      /* webpackChunkName: "transaction_post_tax_card_detail" */ 'components/TransactionDetailPretaxCard'
    )
);

//Subscription
const ProfileSubscriptionDetail = lazy(
  () =>
    import(
      /* webpackChunkName: "subscription" */ 'components/ProfileSubscriptionDetail'
    )
);

const SettingsReimbursementPage = lazy(
  () =>
    import(
      /* webpackChunkName: "settings_reimbursement" */ 'components/SettingsReimbursement'
    )
);
const SettingsPaymentPage = lazy(
  () =>
    import(
      /* webpackChunkName: "settings_payment" */ 'components/SettingsPayment'
    )
);
const SettingsIntegrationPage = lazy(
  () =>
    import(
      /* webpackChunkName: "settings_integrations" */ 'components/SettingsIntegration'
    )
);
const SettingsNotificationPage = lazy(
  () =>
    import(
      /* webpackChunkName: "settings_integrations" */ 'components/SettingsNotification'
    )
);

const DependentsListPage = lazy(
  () =>
    import(
      /* webpackChunkName: "settings_dependents" */ 'components/DependentsList'
    )
);

const DependentViewOrEditPage = lazy(
  () =>
    import(
      /* webpackChunkName: "dependent_view_or_edit" */ 'components/DependentViewOrEdit'
    )
);

const DependentNewPage = lazy(
  () =>
    import(/* webpackChunkName: "dependent_new" */ 'components/DependentNew')
);

const SettingsSecurityPage = lazy(
  () =>
    import(
      /* webpackChunkName: "settings_integrations" */ 'components/SettingsSecurity'
    )
);
const SettingsPosttaxCardsNewPage = lazy(
  () =>
    import(
      /* webpackChunkName: "settings_post_tax_new_card" */ 'components/SettingsPosttaxCardsNew'
    )
);
const SettingsPosttaxCardsListPage = lazy(
  () =>
    import(
      /* webpackChunkName: "settings_post_tax_card_list" */ 'components/SettingsPosttaxCardsList'
    )
);

const SettingsPosttaxCardsReplacePage = lazy(
  () =>
    import(
      /* webpackChunkName: "settings_post_tax_card_replace" */ 'components/SettingsPosttaxCardsReplace'
    )
);
const SettingsPosttaxCardsEditPage = lazy(
  () =>
    import(
      /* webpackChunkName: "settings_post_tax_card_edit" */ 'components/SettingsPosttaxCardsEdit'
    )
);
const SettingsPosttaxCardsSuccessPage = lazy(
  () =>
    import(
      /* webpackChunkName: "settings_post_tax_card_success" */ 'components/SettingsPosttaxCardsSuccess'
    )
);

const SettingsPretaxCardNewPage = lazy(
  () =>
    import(/* webpackChunkName: "wallet" */ 'components/SettingsPretaxCardNew')
);
const SettingsPretaxCardListPage = lazy(
  () =>
    import(
      /* webpackChunkName: "settings_card" */ 'components/SettingsPretaxCardList'
    )
);
const SettingsPretaxDependentPage = lazy(
  () =>
    import(
      /* webpackChunkName: "settings_card" */ 'components/SettingsPretaxDependent'
    )
);

//Checkout
const CheckoutPage = lazy(
  () => import(/* webpackChunkName: "checkout" */ 'components/Checkout')
);

//Slack auth
const SlackAuthPage = lazy(
  () => import(/* webpackChunkName: "slack" */ 'components/SlackAuth')
);
const SlackInstallPage = lazy(
  () =>
    import(/* webpackChunkName: "slack_install" */ 'components/SlackInstall')
);
const HomePage = lazy(
  () => import(/* webpackChunkName: "home" */ 'components/Home')
);

const HomeFeedPage = lazy(
  () => import(/* webpackChunkName: "home_feed" */ 'components/HomeFeed')
);

const SAMLPage = lazy(
  () => import(/* webpackChunkName: "saml" */ 'components/Saml')
);

// products
const ProductListExplorePage = lazy(
  () =>
    import(
      /* webpackChunkName: "product_list_explore_experiment" */ 'components/ProductListExplore'
    )
);
const ProductListCategoryPage = lazy(
  () =>
    import(
      /* webpackChunkName: "product_list_category" */ 'components/ProductListCategory'
    )
);
const CategoryLandingPage = lazy(
  () =>
    import(
      /* webpackChunkName: "category_landing" */ 'components/CategoryLanding'
    )
);
const CategoryBrowserPage = lazy(
  () =>
    import(
      /* webpackChunkName: "category_browser" */ 'components/CategoryBrowser'
    )
);
const ProductListFeaturedPage = lazy(
  () =>
    import(
      /* webpackChunkName: "product_list_featured" */ 'components/ProductListFeatured'
    )
);
const ProductListCollectionPage = lazy(
  () =>
    import(
      /* webpackChunkName: "product_list_collection" */ 'components/ProductListCollection'
    )
);
const ProductPage = lazy(
  () => import(/* webpackChunkName: "product" */ 'components/ProductPage')
);

const VendorListVenuesPage = lazy(
  () =>
    import(
      /* webpackChunkName: "vendor_list_venues" */ 'components/VendorListVenues'
    )
);

const StoreLandingPage = lazy(
  () =>
    import(
      /* webpackChunkName: "product_list_explore_experiment" */ 'components/StoreLandingPage'
    )
);

const Landing2023NYPage = lazy(
  () =>
    import(/* webpackChunkName: "landing_2023_ny" */ 'components/Landing2023NY')
);

const ErrorFallbackPage = lazy(
  () =>
    import(/* webpackChunkName: "error_fallback" */ 'components/ErrorFallback')
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 0,
      refetchOnWindowFocus: false,
    },
  },
});

const PrivateRoute = ({ children }: { children: JSX.Element }) =>
  AuthUtils.isAuthCookiePresent() ? (
    children
  ) : (
    <Navigate to={RootRoutes.login()} />
  );

type ExperimentalRouteProps = {
  element: JSX.Element;
  fallback_component: JSX.Element;
  feature_flag: FeatureFlag;
  feature_flag_type?: string;
};

const ExperimentalRoute = ({
  element,
  fallback_component,
  feature_flag,
  feature_flag_type = 'boolean',
}: ExperimentalRouteProps) => {
  const context = useContext(FeatureFlagContext);
  const feature_flag_default = feature_flag_type === 'boolean' ? false : 'off';
  const feature_flag_value = context?.flag(feature_flag, feature_flag_default);

  // for string flags, segments including `control` or `off` = not in experiment
  const show_experimental_page =
    feature_flag_type === 'string'
      ? !feature_flag_value.includes('control') &&
        !feature_flag_value.includes('off')
      : feature_flag_value;

  return show_experimental_page ? element : fallback_component;
};

const routesWithFullScreenLayout = () => {
  const getBackPath = ({ params }: { params: Params }) => {
    if (params.account_id) {
      return AccountRoutes.detailPretax({
        flex_account_id: params?.account_id,
      });
    }
    throw new Error('account id missing in path');
  };

  return (
    <Route
      element={
        <CurrentUserContextProvider>
          <PretaxContextProvider>
            <FullScreenLayout getBackPathFromParams={getBackPath} />
          </PretaxContextProvider>
        </CurrentUserContextProvider>
      }
    >
      <Route
        path="accounts/:account_id/enroll-hsa"
        element={
          <PrivateRoute>
            <AccountEnrollHsaPage />
          </PrivateRoute>
        }
      />
    </Route>
  );
};

const routesWithEnrollLayout = () => (
  <Route
    element={
      <CurrentUserContextProvider>
        <PretaxContextProvider>
          <BaseLayout>
            <EnrollLayout />
          </BaseLayout>
        </PretaxContextProvider>
      </CurrentUserContextProvider>
    }
  >
    <Route
      path="accounts/pretax/commuter-enroll"
      element={
        <PrivateRoute>
          <AccountEnrollPretaxPage />
        </PrivateRoute>
      }
    />
    <Route
      path="accounts/pretax/commuter-enroll-review"
      element={
        <PrivateRoute>
          <AccountEnrollReviewPretaxPage />
        </PrivateRoute>
      }
    />
    <Route
      path="accounts/pretax/commuter-enroll-confirmed"
      element={
        <PrivateRoute>
          <AccountEnrollConfirmedPretaxPage />
        </PrivateRoute>
      }
    />
  </Route>
);

const routesWithStoreLayout = () => (
  <Route
    element={
      <CurrentUserContextProvider>
        <PretaxContextProvider>
          <BaseLayout>
            <ExperimentalRoute
              feature_flag={FeatureFlag.STORE_LANDING_PAGE}
              element={
                <StoreLandingCompactLayout>
                  <StoreLayout />
                </StoreLandingCompactLayout>
              }
              fallback_component={
                <StoreWithBannerLayout>
                  <StoreLayout />
                </StoreWithBannerLayout>
              }
              feature_flag_type={'string'}
            />
          </BaseLayout>
        </PretaxContextProvider>
      </CurrentUserContextProvider>
    }
  >
    <Route
      path="/explore/accounts/:employee_wallet_id/featured/:featured_section"
      element={
        <PrivateRoute>
          <ProductListFeaturedPage />
        </PrivateRoute>
      }
    />
    <Route
      path="/explore/pretax_accounts/:pretax_account"
      element={
        <PrivateRoute>
          <PretaxStoreDetailPage />
        </PrivateRoute>
      }
    />
    <Route
      path="/explore/gym-and-fitness"
      element={
        <PrivateRoute>
          <VendorListVenuesPage />
        </PrivateRoute>
      }
    />
    <Route
      path="/search"
      element={
        <PrivateRoute>
          <ProductSearchResultsPage />
        </PrivateRoute>
      }
    />
    <Route
      path="/explore/collection"
      element={
        <PrivateRoute>
          <ProductListCollectionPage />
        </PrivateRoute>
      }
    />
  </Route>
);

const routesWithStoreLandingLayout = () => (
  <Route
    element={
      <CurrentUserContextProvider>
        <PretaxContextProvider>
          <BaseLayout>
            <ExperimentalRoute
              feature_flag={FeatureFlag.STORE_LANDING_PAGE}
              element={
                <StoreLandingCompactLayout>
                  <StoreLandingLayout />
                </StoreLandingCompactLayout>
              }
              fallback_component={
                <StoreWithBannerLayout>
                  <StoreLayout />
                </StoreWithBannerLayout>
              }
              feature_flag_type={'string'}
            />
          </BaseLayout>
        </PretaxContextProvider>
      </CurrentUserContextProvider>
    }
  >
    <Route
      path="/explore"
      element={
        <ExperimentalRoute
          feature_flag={FeatureFlag.STORE_LANDING_PAGE}
          element={
            <PrivateRoute>
              <StoreLandingPage />
            </PrivateRoute>
          }
          fallback_component={
            <PrivateRoute>
              <ProductListExplorePage />
            </PrivateRoute>
          }
          feature_flag_type={'string'}
        />
      }
    />
  </Route>
);

const routesWithCategoryLayout = () => (
  <Route
    element={
      <CurrentUserContextProvider>
        <PretaxContextProvider>
          <BaseLayout>
            <ExperimentalRoute
              feature_flag={FeatureFlag.HOME_OFFICE_CATEGORY}
              element={
                <StoreLandingCompactLayout>
                  <StoreLandingLayout />
                </StoreLandingCompactLayout>
              }
              fallback_component={
                <StoreWithBannerLayout>
                  <StoreLayout />
                </StoreWithBannerLayout>
              }
              feature_flag_type={'string'}
            />
          </BaseLayout>
        </PretaxContextProvider>
      </CurrentUserContextProvider>
    }
  >
    <Route
      path="/explore/accounts/:employee_wallet_id/products/:category"
      element={
        <ExperimentalRoute
          feature_flag={FeatureFlag.HOME_OFFICE_CATEGORY}
          element={
            <PrivateRoute>
              <CategoryBrowserPage />
            </PrivateRoute>
          }
          fallback_component={
            <PrivateRoute>
              <ProductListCategoryPage />
            </PrivateRoute>
          }
          feature_flag_type={'string'}
        />
      }
    />
  </Route>
);

const routesWithWelcomeLayout = () => (
  <Route
    element={
      <CurrentUserContextProvider>
        <PretaxContextProvider>
          <WelcomeLayout />
        </PretaxContextProvider>
      </CurrentUserContextProvider>
    }
  >
    <Route
      path="/welcome/member-onboarding"
      element={
        <PrivateRoute>
          <MemberOnboarding />
        </PrivateRoute>
      }
    />
    <Route
      path="/welcome/member-onboarding/review"
      element={
        <PrivateRoute>
          <MemberOnboardingReview />
        </PrivateRoute>
      }
    />
    <Route
      path="/welcome/member-onboarding/activate"
      element={
        <PrivateRoute>
          <MemberOnboardingActivate />
        </PrivateRoute>
      }
    />
  </Route>
);

const routesWithCardSettingsLayout = () => (
  <Route
    element={
      <CurrentUserContextProvider>
        <PretaxContextProvider>
          <BaseLayout>
            <SettingsLayout>
              <CardsLayout />
            </SettingsLayout>
          </BaseLayout>
        </PretaxContextProvider>
      </CurrentUserContextProvider>
    }
  >
    <Route
      path="/settings/employer-sponsored/cards"
      element={
        <PrivateRoute>
          <SettingsPosttaxCardsListPage />
        </PrivateRoute>
      }
    />
    <Route
      path="/settings/employer-sponsored/cards/settings"
      element={
        <PrivateRoute>
          <SettingsPosttaxCardsEditPage />
        </PrivateRoute>
      }
    />
  </Route>
);

const routesWithLinkAccountSettingsLayout = () => (
  <Route
    element={
      <CurrentUserContextProvider>
        <PretaxContextProvider>
          <BaseLayout>
            <SettingsLayout>
              <SettingsLinkAccountsLayout />
            </SettingsLayout>
          </BaseLayout>
        </PretaxContextProvider>
      </CurrentUserContextProvider>
    }
  >
    <Route
      path="/settings/bank-accounts"
      element={
        <PrivateRoute>
          <SettingsReimbursementPage />
        </PrivateRoute>
      }
    />
    <Route
      path="/settings/payment"
      element={
        <PrivateRoute>
          <SettingsPaymentPage />
        </PrivateRoute>
      }
    />
  </Route>
);

const routesWithSettingsLayout = () => (
  <Route
    element={
      <CurrentUserContextProvider>
        <PretaxContextProvider>
          <BaseLayout>
            <SettingsLayout />
          </BaseLayout>
        </PretaxContextProvider>
      </CurrentUserContextProvider>
    }
  >
    <Route path="/settings" element={<Navigate to={'/settings/profile'} />} />
    <Route
      path="/settings/profile"
      element={
        <PrivateRoute>
          <SettingsProfilePage />
        </PrivateRoute>
      }
    />
    <Route
      path="/settings/employer-sponsored/cards/new"
      element={
        <PrivateRoute>
          <SettingsPosttaxCardsNewPage />
        </PrivateRoute>
      }
    />
    <Route
      path="/settings/pretax/cards"
      element={
        <PrivateRoute>
          <SettingsPretaxCardListPage />
        </PrivateRoute>
      }
    />
    <Route
      path="/settings/pretax/cards/new"
      element={
        <PrivateRoute>
          <SettingsPretaxCardNewPage />
        </PrivateRoute>
      }
    />
    <Route
      path="/settings/pretax/dependents/:dependent_id"
      element={
        <PrivateRoute>
          <SettingsPretaxDependentPage />
        </PrivateRoute>
      }
    />

    <Route
      path="settings/dependents"
      element={
        <PrivateRoute>
          <DependentsListPage />
        </PrivateRoute>
      }
    />
    <Route
      path="/settings/integrations"
      element={
        <PrivateRoute>
          <SettingsIntegrationPage />
        </PrivateRoute>
      }
    />
    <Route
      path="/settings/notifications"
      element={
        <PrivateRoute>
          <SettingsNotificationPage />
        </PrivateRoute>
      }
    />
    <Route
      path="/settings/security"
      element={
        <PrivateRoute>
          <SettingsSecurityPage />
        </PrivateRoute>
      }
    />
    <Route
      path="/settings/employer-sponsored/cards/:card_id/replace"
      element={
        <PrivateRoute>
          <SettingsPosttaxCardsReplacePage />
        </PrivateRoute>
      }
    />
    <Route
      path="/settings/forma-card"
      element={
        <PrivateRoute>
          <FormaCardListPage />
        </PrivateRoute>
      }
    />
  </Route>
);

const routesWithProfileLayout = () => (
  <Route
    element={
      <CurrentUserContextProvider>
        <PretaxContextProvider>
          <BaseLayout>
            <ProfileLayout />
          </BaseLayout>
        </PretaxContextProvider>
      </CurrentUserContextProvider>
    }
  >
    <Route
      path="/history/subscriptions"
      element={
        <PrivateRoute>
          <ProfileSubscriptionList />
        </PrivateRoute>
      }
    />
    <Route
      path="/history/orders"
      element={
        <PrivateRoute>
          <ProfileOrderListPage />
        </PrivateRoute>
      }
    />
  </Route>
);

const routesWithBaseLayout = () => (
  <Route
    element={
      <CurrentUserContextProvider>
        <PretaxContextProvider>
          <BaseLayout />
        </PretaxContextProvider>
      </CurrentUserContextProvider>
    }
  >
    <Route
      path="/home"
      element={
        <PrivateRoute>
          <HomePage />
        </PrivateRoute>
      }
    />
    <Route
      path="/"
      element={
        <PrivateRoute>
          <HomePage />
        </PrivateRoute>
      }
    />
    <Route
      path="/home-feed"
      element={
        <PrivateRoute>
          <HomeFeedPage />
        </PrivateRoute>
      }
    />
    <Route
      path="/explore/search"
      element={
        <PrivateRoute>
          <VendorVenueSearchPage />
        </PrivateRoute>
      }
    />
    <Route
      path="/explore/:vendor_id/:product_id"
      element={
        <PrivateRoute>
          <ProductPage />
        </PrivateRoute>
      }
    />
    <Route
      path="/accounts"
      element={
        <PrivateRoute>
          <AccountListPage />
        </PrivateRoute>
      }
    />
    <Route
      path="/accounts/more-info"
      element={
        <PrivateRoute>
          <AccountListPage />
        </PrivateRoute>
      }
    />

    <Route
      path="accounts/:account_id/enroll-hsa-success"
      element={
        <PrivateRoute>
          <AccountEnrollHsaSuccessPage />
        </PrivateRoute>
      }
    />

    <Route
      path="/accounts/pretax/:flex_account_id"
      element={
        <PrivateRoute>
          <AccountDetailPretaxPage />
        </PrivateRoute>
      }
    />

    <Route
      path="/accounts/pretax/:flex_account_id/HSA/expense/new"
      element={
        <PrivateRoute>
          <HsaExpenseFormContextProvider>
            <HsaExpenseNew />
          </HsaExpenseFormContextProvider>
        </PrivateRoute>
      }
    />

    <Route
      path="/accounts/pretax/:flex_account_id/HSA/expense/confirm"
      element={
        <PrivateRoute>
          <HsaExpenseFormContextProvider>
            <HsaExpenseConfirm />
          </HsaExpenseFormContextProvider>
        </PrivateRoute>
      }
    />

    <Route
      path="/accounts/pretax/:flex_account_id/HSA/reimbursement/new"
      element={
        <PrivateRoute>
          <HsaReimbursementFormContextProvider>
            <HsaReimbursementNew />
          </HsaReimbursementFormContextProvider>
        </PrivateRoute>
      }
    />

    <Route
      path="/accounts/pretax/:flex_account_id/HSA/reimbursement/confirm"
      element={
        <PrivateRoute>
          <HsaReimbursementFormContextProvider>
            <HsaReimbursementConfirm />
          </HsaReimbursementFormContextProvider>
        </PrivateRoute>
      }
    />

    <Route
      path="/accounts/employer-sponsored/:wallet_id"
      element={
        <PrivateRoute>
          <AccountDetailEmployerSponsored />
        </PrivateRoute>
      }
    />

    <Route
      path="/dependents/new"
      element={
        <PrivateRoute>
          <DependentNewPage />
        </PrivateRoute>
      }
    />

    <Route
      path="/dependents/:dependent_id"
      element={
        <PrivateRoute>
          <DependentViewOrEditPage />
        </PrivateRoute>
      }
    />

    <Route
      path="/claims"
      element={
        <PrivateRoute>
          <ClaimListPage />
        </PrivateRoute>
      }
    />
    <Route
      path="/claims/employer-sponsored/:claim_id"
      element={
        <PrivateRoute>
          <ClaimDetailEmployerSponsoredPage />
        </PrivateRoute>
      }
    />
    <Route
      path="/claims/pretax-claim/:claim_id"
      element={
        <PrivateRoute>
          <ClaimDetailPretaxClaimPage />
        </PrivateRoute>
      }
    />
    <Route
      path="/claims/pretax-transaction/:transaction_id"
      element={
        <PrivateRoute>
          <ClaimDetailPretaxTransactionPage />
        </PrivateRoute>
      }
    />
    <Route
      path="/claims/new"
      element={
        <PrivateRoute>
          <ClaimNewPage />
        </PrivateRoute>
      }
    />
    <Route
      path="/claims/new/:account_id"
      element={
        <PrivateRoute>
          <ClaimNewPage />
        </PrivateRoute>
      }
    />
    <Route
      path="/claims/success"
      element={
        <PrivateRoute>
          <ClaimSuccessPage />
        </PrivateRoute>
      }
    />

    <Route
      path="/orders/:transaction_id"
      element={
        <PrivateRoute>
          <ProfileOrderDetail />
        </PrivateRoute>
      }
    />

    <Route
      path="/subscriptions/:subscription_id/payments-history"
      element={
        <PrivateRoute>
          <ProfileSubscriptionDetail />
        </PrivateRoute>
      }
    />

    <Route
      path="/subscriptions/:subscription_id/manage-membership"
      element={
        <PrivateRoute>
          <ProfileSubscriptionDetail />
        </PrivateRoute>
      }
    />

    <Route
      path="/orders/card-transactions/:id"
      element={
        <PrivateRoute>
          <TransactionDetailPosttaxCardPage />
        </PrivateRoute>
      }
    />

    <Route
      path="/pretax/card-transactions/:reference_id"
      element={
        <PrivateRoute>
          <TransactionDetailPretaxCardPage />
        </PrivateRoute>
      }
    />

    <Route
      path="/checkout"
      element={
        <PrivateRoute>
          <CheckoutPage />
        </PrivateRoute>
      }
    />
    <Route
      path="/settings/employer-sponsored/cards/:stripe_card_id/success"
      element={
        <PrivateRoute>
          <SettingsPosttaxCardsSuccessPage />
        </PrivateRoute>
      }
    />
    <Route
      path="/slack/auth"
      element={
        <PrivateRoute>
          <SlackAuthPage />
        </PrivateRoute>
      }
    />
    <Route
      path="/slack/install"
      element={
        <PrivateRoute>
          <SlackInstallPage />
        </PrivateRoute>
      }
    />
    <Route
      path="/accounts/hsa-investment/:account_id"
      element={
        <PrivateRoute>
          <ExperimentalRoute
            feature_flag={FeatureFlag.HSA_INVESTMENTS}
            element={<HsaInvestmentDetailPage />}
            fallback_component={<HomePage />}
          />
        </PrivateRoute>
      }
    />
    <Route
      path="/accounts/hsa-investment/:account_id/invest-money"
      element={
        <PrivateRoute>
          <ExperimentalRoute
            feature_flag={FeatureFlag.HSA_INVESTMENTS}
            element={<HsaInvestMoneyPage />}
            fallback_component={<HomePage />}
          />
        </PrivateRoute>
      }
    />
    <Route
      path="accounts/:account_id/invest-success"
      element={
        <PrivateRoute>
          <HsaInvestmentSuccessPage />
        </PrivateRoute>
      }
    />
    <Route
      path="accounts/:account_id/invest-later"
      element={
        <PrivateRoute>
          <HsaInvestmentDeferralPage />
        </PrivateRoute>
      }
    />
    <Route
      path="/accounts/hsa-investment/:account_id/documents"
      element={
        <PrivateRoute>
          <ExperimentalRoute
            feature_flag={FeatureFlag.HSA_INVESTMENTS}
            element={<HsaInvestmentDocumentListPage />}
            fallback_component={<HomePage />}
          />
        </PrivateRoute>
      }
    />
    <Route
      path="/accounts/hsa-cash/:account_id/documents"
      element={
        <PrivateRoute>
          <ExperimentalRoute
            feature_flag={FeatureFlag.HSA_INVESTMENTS}
            element={<HsaCashDocumentListPage />}
            fallback_component={<HomePage />}
          />
        </PrivateRoute>
      }
    />
    <Route
      path="/accounts/hsa-investment/sell-orders/:member_id"
      element={
        <PrivateRoute>
          <ExperimentalRoute
            feature_flag={FeatureFlag.HSA_INVESTMENTS}
            element={<HsaSellOrders />}
            fallback_component={<HomePage />}
          />
        </PrivateRoute>
      }
    />
    <Route
      path="/accounts/hsa-investment/sell-orders-confirmation/:member_id/:sell_order/:amount"
      element={
        <PrivateRoute>
          <ExperimentalRoute
            feature_flag={FeatureFlag.HSA_INVESTMENTS}
            element={<HsaSellOrdersConfirmation />}
            fallback_component={<HomePage />}
          />
        </PrivateRoute>
      }
    />

    <Route
      path="/accounts/hsa-transfer"
      element={
        <PrivateRoute>
          <ExperimentalRoute
            feature_flag={FeatureFlag.ALEGEUS_HSA_ENABLED}
            element={<AlegeusHsaBulkTransferPage />}
            fallback_component={<HomePage />}
          />
        </PrivateRoute>
      }
    />
    <Route
      path="/accounts/hsa-transfer/decline"
      element={
        <PrivateRoute>
          <ExperimentalRoute
            feature_flag={FeatureFlag.ALEGEUS_HSA_ENABLED}
            element={<AlegeusHsaBulkTransferDeclinePage />}
            fallback_component={<HomePage />}
          />
        </PrivateRoute>
      }
    />

    <Route
      path="/explore/landing2023ny"
      element={
        <PrivateRoute>
          <Landing2023NYPage />
        </PrivateRoute>
      }
    />

    {/* this page needs to be in the base layout because part of the page is full-width*/}
    <Route
      path="/explore/accounts/:walletId/products/landing/:categoryId"
      element={
        <StoreLandingCompactLayout>
          <PrivateRoute>
            <CategoryLandingPage />
          </PrivateRoute>
        </StoreLandingCompactLayout>
      }
    />
  </Route>
);

const routesWithNoLayout = () => (
  <Route
    element={
      <CurrentUserContextProvider>
        <Outlet />
      </CurrentUserContextProvider>
    }
  >
    <Route
      path="/external_store/:account_type/:store_name"
      element={
        <PrivateRoute>
          <ExternalStoreLinkPage />
        </PrivateRoute>
      }
    />
    <Route
      path="/saml/alegeus"
      element={
        <PrivateRoute>
          <SAMLPage />
        </PrivateRoute>
      }
    />
  </Route>
);

const publicRoutes = () => (
  <Route element={<LoginLayout />}>
    <Route path="/login" element={<LoginPage />} />
    <Route path="/login/password-reset" element={<LoginPasswordResetPage />} />
    <Route path="/auth/:type" element={<Authentication />} />
    <Route path="/:companyId/activate" element={<MemberActivationPage />} />
    <Route path="/activate/success" element={<MemberActivationSuccessPage />} />
  </Route>
);

const AllRoutes = () => (
  <Sentry.ErrorBoundary>
    <FeatureFlagContextProvider>
      <IntlProvider locale={'en'}>
        <ThemeProvider theme={light_theme}>
          <QueryClientProvider client={queryClient}>
            <BrowserRouter>
              <ScrollToTop>
                <Suspense fallback={<Spinner />}>
                  <DeployListenerLayout>
                    <ErrorBoundary
                      FallbackComponent={ErrorFallbackPage}
                      onError={(e) => Sentry.captureException(e)}
                    >
                      <Routes>
                        {publicRoutes()}
                        {routesWithEnrollLayout()}
                        {routesWithFullScreenLayout()}
                        {routesWithWelcomeLayout()}
                        {routesWithStoreLayout()}
                        {routesWithStoreLandingLayout()}
                        {routesWithCategoryLayout()}
                        {routesWithSettingsLayout()}
                        {routesWithCardSettingsLayout()}
                        {routesWithNoLayout()}
                        {routesWithBaseLayout()}
                        {routesWithProfileLayout()}
                        {routesWithLinkAccountSettingsLayout()}
                        {/* Catch all route MUST be last */}
                        <Route
                          path="*"
                          element={
                            <ErrorFallbackPage
                              error={new Error('Page not found')}
                              resetErrorBoundary={console.log}
                            />
                          }
                        />
                      </Routes>
                    </ErrorBoundary>
                  </DeployListenerLayout>
                </Suspense>
              </ScrollToTop>
            </BrowserRouter>
            <ReactQueryDevtools />
          </QueryClientProvider>
        </ThemeProvider>
      </IntlProvider>
    </FeatureFlagContextProvider>
  </Sentry.ErrorBoundary>
);

export default AllRoutes;
